import React, { useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { toast } from "react-toastify";

import { CrmPetController } from "../../../services/controllers/crmPet";
import {
  buildContactMessage,
  buildRuleContactMessage,
  CRM_PET_RULES,
} from "../../../services/formatResponses/crmPet";
import { formatTutorName } from "../../../services/functions";
import { useCrmPet } from "../../../store/crmPet/useCrmPet";
import { WHATSAPP_SEND_MESSAGE_BASE } from "../../../styles/constant";
import { WhatsAppIcon } from "../icons/WhatsAppIcon";

const PARENT_RULE_CODES = new Set(Object.values(CRM_PET_RULES));

export function WhatsAppModal({ customer, config, loading, error, activeRule = "ALL", onClose }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="crm-pet-whatsapp-title">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {customer ? <WhatsAppContent key={`${customer.customerId}:${activeRule}`} customer={customer} config={config} activeRule={activeRule} onClose={onClose} /> : (
          <div className="p-5">
            <div className="flex items-center justify-between">
              <p id="crm-pet-whatsapp-title" className="font-semibold">WhatsApp</p>
              <button type="button" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
            </div>
            <p role={error ? "alert" : "status"}>{loading ? "Carregando pets e oportunidades do tutor..." : error || "Nenhum pet disponível."}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function WhatsAppContent({ customer, config, activeRule, onClose }) {
  const controller = CrmPetController();
  const submitting = useCrmPet((state) => state.data.contactSubmitting);
  const inFlight = useRef(false);
  const customerPetIds = customer.pets.map((pet) => pet.petId);
  const availablePets = customer.pets.filter((pet) => !pet.blocked);
  const availableIds = availablePets.map((pet) => pet.petId);
  const availableMemberships = customer.memberships.filter((item) => (
    PARENT_RULE_CODES.has(item.ruleCode)
    && item.petIds.some((id) => customerPetIds.includes(id))
  ));
  const fixedRule = activeRule !== "ALL";
  const initialMemberships = activeRule === "ALL"
    ? availableMemberships
    : availableMemberships.filter((membership) => membership.ruleCode === activeRule);
  const initialMembershipIds = initialMemberships.map((membership) => membership.membershipId);
  const initialPetIds = activeRule === "ALL"
    ? [...new Set(availableMemberships.flatMap((membership) => membership.petIds))]
      .filter((petId) => availableIds.includes(petId))
    : [...new Set(initialMemberships.flatMap((membership) => membership.petIds))]
      .filter((petId) => availableIds.includes(petId));
  const [selectedMembershipIds, setSelectedMembershipIds] = useState(() => initialMembershipIds);
  const [selectedPetIds, setSelectedPetIds] = useState(() => initialPetIds);
  const [mode, setMode] = useState("UNIFIED");
  const [edits, setEdits] = useState({});
  const [sentPets, setSentPets] = useState([]);
  const selectedPets = availablePets.filter((pet) => selectedPetIds.includes(pet.petId));
  const selectedMemberships = fixedRule
    ? initialMemberships
    : availableMemberships.filter((item) => selectedMembershipIds.includes(item.membershipId) && item.petIds.some((id) => selectedPetIds.includes(id)));
  const phone = (customer.customerPhone || "").replace(/\D/g, "");
  const groups = mode === "UNIFIED" ? [{ key: "unified", pets: selectedPets }] : selectedPets.map((pet) => ({ key: pet.petId, pets: [pet] }));
  const messages = groups.map(({ key, pets }) => {
    const memberships = fixedRule
      ? selectedMemberships
      : selectedMemberships.filter((item) => pets.some((pet) => item.petIds.includes(pet.petId)));
    const generated = fixedRule
      ? buildRuleContactMessage({ customer, ruleCode: activeRule, memberships, pets, config })
      : buildContactMessage({ customer, memberships, pets, config });
    const message = edits[key] ?? generated;
    const covered = fixedRule || pets.every((pet) => memberships.some((item) => item.petIds.includes(pet.petId)));
    const sent = pets.some((pet) => sentPets.includes(pet.petId));
    return { key, pets, memberships, message, sent, canSend: Boolean(phone && customer.customerId && pets.length && memberships.length && covered && message.trim() && message.length <= 4096 && !submitting && !sent) };
  });

  const togglePet = (petId) => {
    const removing = selectedPetIds.includes(petId);
    const nextIds = removing ? selectedPetIds.filter((id) => id !== petId) : [...selectedPetIds, petId];
    setSelectedPetIds(nextIds);
    if (!fixedRule) {
      setSelectedMembershipIds((current) => {
        const next = removing ? current : [...new Set([...current, ...availableMemberships.filter((item) => item.petIds.includes(petId)).map((item) => item.membershipId)])];
        return next.filter((id) => availableMemberships.find((item) => item.membershipId === id)?.petIds.some((pet) => nextIds.includes(pet)));
      });
    }
    setEdits({});
  };

  const send = async (item) => {
    if (!item.canSend || inFlight.current) return;
    const popup = window.open("", "_blank");
    if (!popup) {
      toast.error("Permita pop-ups para abrir o WhatsApp.", { theme: "colored" });
      return;
    }
    inFlight.current = true;
    try {
      popup.document.title = "Abrindo WhatsApp...";
      popup.document.body.textContent = "Registrando contato...";
      const ids = item.pets.map((pet) => pet.petId);
      const result = fixedRule
        ? await controller.registerRuleContact(customer, ids, activeRule, item.message)
        : await controller.registerUnifiedContact(customer, ids, item.memberships.map((membership) => membership.membershipId), item.message);
      if (!result) { popup.close(); return; }
      setSentPets((current) => [...new Set([...current, ...ids])]);
      const url = WHATSAPP_SEND_MESSAGE_BASE.replace("[PHONE_NUMBER]", phone).replace("[MESSAGE]", encodeURIComponent(item.message));
      popup.opener = null;
      popup.location.replace(url);
      if (mode === "UNIFIED") onClose();
    } catch {
      popup.close();
      toast.error("Não foi possível abrir o WhatsApp. Confira o histórico antes de tentar novamente.", { theme: "colored" });
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
        <p id="crm-pet-whatsapp-title" className="m-0 flex items-center gap-2 text-sm font-semibold"><WhatsAppIcon size={16} /> WhatsApp para {formatTutorName(customer.customerName)}</p>
        <button type="button" onClick={onClose} disabled={submitting} aria-label="Fechar"><X size={18} /></button>
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
        <SelectionBox title="Pets relacionados">
          {customer.pets.map((pet) => (
            <CheckOption key={pet.petId} checked={selectedPetIds.includes(pet.petId)} disabled={pet.blocked || submitting} onChange={() => togglePet(pet.petId)} label={pet.petName || "Sem nome"} detail={pet.blocked ? "Bloqueado" : null} />
          ))}
        </SelectionBox>
        <fieldset className="flex gap-4" disabled={submitting}>
          <legend className="mb-2 text-xs font-semibold">Forma de mensagem</legend>
          {[["UNIFIED", "Mensagem única"], ["SEPARATE", "Mensagem por pet"]].map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input type="radio" name="crm-message-mode" value={value} checked={mode === value} onChange={() => setMode(value)} />{label}
            </label>
          ))}
        </fieldset>
        {messages.map((item) => (
          <div key={item.key} className="rounded-lg border border-zinc-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor={`crm-message-${item.key}`} className="text-sm font-semibold">{mode === "UNIFIED" ? "Mensagem" : `Mensagem para ${item.pets[0].petName}`}</label>
              {edits[item.key] !== undefined && <button type="button" disabled={submitting || item.sent} onClick={() => setEdits((current) => { const next = { ...current }; delete next[item.key]; return next; })} className="text-xs underline">Restaurar</button>}
            </div>
            <textarea id={`crm-message-${item.key}`} rows={7} maxLength={4096} value={item.message} disabled={submitting || item.sent} onChange={(event) => setEdits({ ...edits, [item.key]: event.target.value })} className="w-full rounded-lg border border-zinc-200 p-3 text-sm focus:ring-2 focus:ring-scale-300" />
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-500">{item.message.length}/4096 caracteres</span>
              <button type="button" onClick={() => send(item)} disabled={!item.canSend} className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {submitting ? <RefreshCw size={14} className="animate-spin" /> : <WhatsAppIcon size={14} />}
                {item.sent ? "Contato registrado" : "Abrir WhatsApp"}
              </button>
            </div>
          </div>
        ))}
        {!selectedPets.length && <p role="status" className="text-sm">Selecione ao menos um pet ativo.</p>}
        {!phone && <p role="alert" className="text-sm text-red-600">Cliente sem telefone cadastrado.</p>}
      </div>
      <div className="flex justify-end border-t border-zinc-200 px-5 py-4"><button type="button" onClick={onClose} disabled={submitting}>Fechar</button></div>
    </>
  );
}

function SelectionBox({ title, children }) {
  return <fieldset className="rounded-lg border border-zinc-200 p-3"><legend className="px-1 text-xs font-semibold">{title}</legend><div className="grid gap-2 sm:grid-cols-2">{children}</div></fieldset>;
}

function CheckOption({ checked, disabled, onChange, label, detail }) {
  return <label className={`flex items-center gap-2 text-xs ${disabled ? "opacity-60" : "cursor-pointer"}`}><input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} className="h-4 w-4 accent-scale-500" /><span>{label}</span>{detail && <span>{detail}</span>}</label>;
}
